"use client";

import Footer from "@/components/Footer";
import Navigation from "@/components/Navigation";
import StructuredData, { localBusinessSchema } from "@/components/StructuredData";
import { GlowingEffect } from '@/components/ui/glowing-effect';
import { Input } from '@/components/ui/input';
import { AlertCircle, CheckCircle2, Loader2, Mail, MapPin, Phone, Send } from "lucide-react";
import { useEffect, useState } from "react";


export default function ContactClient() {
  const [showEnquireForm, setShowEnquireForm] = useState(false);
  const [showBugForm, setShowBugForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    phone: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Load ClickUp script when bug form is shown
  useEffect(() => {
    if (showBugForm && typeof document !== "undefined") {
      const existing = document.getElementById("clickup-script");
      if (!existing) {
        const script = document.createElement("script");
        script.src = "https://app-cdn.clickup.com/assets/js/forms-embed/v1.js";
        script.async = true;
        script.id = "clickup-script";
        document.body.appendChild(script);
      }

      return () => {
        const scriptElement = document.getElementById("clickup-script");
        if (scriptElement?.isConnected) {
          try {
            scriptElement.remove();
          } catch {
            // Ignore cleanup errors if script was already moved/removed
          }
        }
      };
    }
  }, [showBugForm]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear message when user starts typing
    if (submitMessage) {
      setSubmitMessage(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage(null);

    try {
      const response = await fetch("/api/contact/send-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          subject: 'New Contact Form Inquiry', // Hardcoded subject for contact form
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitMessage({
          type: "success",
          text: "Thank you! Your message has been sent successfully.",
        });
        // Reset form
        setFormData({
          name: "",
          email: "",
          message: "",
          phone: "",
        });
      } else {
        setSubmitMessage({
          type: "error",
          text: data.message || "Failed to send message. Please try again.",
        });
      }
    } catch (error) {
      setSubmitMessage({
        type: "error",
        text: "An error occurred. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <StructuredData data={localBusinessSchema} />
      <Navigation />
      <main className="min-h-screen pt-20">
        {/* Elegant Hero Section */}
        <section className="relative py-12 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-semibold mb-3 text-gray-900 dark:text-white">
                Contact Us
            </h1>
              <p className="text-base text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
                Get in touch with us for property management and facility services
            </p>
              <div className="flex justify-center gap-3">
              <button
                  className="px-5 py-2.5 text-sm font-medium bg-maroon-primary text-white rounded-lg hover:bg-maroon-button transition-colors"
                onClick={() => setShowEnquireForm(true)}
              >
                Inquiry Form
              </button>
              <button
                  className="px-5 py-2.5 text-sm font-medium bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 rounded-lg hover:border-maroon-primary dark:hover:border-maroon-primary transition-colors"
                onClick={() => setShowBugForm(true)}
              >
                Submit a Bug
              </button>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form & Info Section */}
        <section className="relative py-12 bg-gray-50 dark:bg-gray-900">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Contact Form */}
                <div className="lg:col-span-2">
                  <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 md:p-8 border border-gray-200 dark:border-gray-700">
                <GlowingEffect disabled={false} />
                <div className="relative z-10">
                      <div className="mb-6">
                        <h2 className="text-2xl font-semibold mb-1.5 dark:text-white">
                    Send us a Message
                  </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          We&apos;ll respond as soon as possible
                        </p>
                      </div>

                      {submitMessage && (
                        <div
                          className={`mb-5 p-3 rounded-lg flex items-start gap-2.5 text-sm ${
                            submitMessage.type === "success"
                              ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800"
                              : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800"
                          }`}
                        >
                          {submitMessage.type === "success" ? (
                            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          ) : (
                            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          )}
                          <p className="font-medium">{submitMessage.text}</p>
                        </div>
                      )}

                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                              Name <span className="text-red-500">*</span>
                      </label>
                            <Input
                        type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              placeholder="Your name"
                              required
                              className="h-10 text-sm"
                      />
                    </div>
                    <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                              Email <span className="text-red-500">*</span>
                      </label>
                            <Input
                        type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              placeholder="your.email@example.com"
                              required
                              className="h-10 text-sm"
                      />
                    </div>
                    </div>
                    <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                            Phone <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                          <Input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="Country code + number, e.g. +974 1234 5678 — we'll reach out to you"
                            className="h-10 text-sm"
                          />
                    </div>
                    <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                            Message <span className="text-red-500">*</span>
                      </label>
                      <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            rows={5}
                            placeholder="Tell us more about your inquiry..."
                            required
                            className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-maroon-primary focus:border-transparent transition-all duration-200 resize-none"
                      />
                    </div>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full h-10 bg-maroon-primary text-white rounded-lg font-medium text-sm hover:bg-maroon-button transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              <span>Sending...</span>
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4" />
                              <span>Send Message</span>
                            </>
                          )}
                    </button>
                  </form>
                </div>
              </div>
            </div>

                {/* Contact Information */}
                <div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold mb-5 dark:text-white">Contact Information</h3>
                    <div className="space-y-4">
                      {/* Address */}
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-maroon-primary/10 dark:bg-maroon-primary/20 flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-4 h-4 text-maroon-primary" />
          </div>
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">Address</h4>
                          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                    Zone 25, Street 330, 7th Floor<br />
                    Buzwair Building No. 53<br />
                    Doha, Qatar
                  </p>
                </div>
                      </div>

                      {/* Phone */}
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-maroon-primary/10 dark:bg-maroon-primary/20 flex items-center justify-center flex-shrink-0">
                          <Phone className="w-4 h-4 text-maroon-primary" />
                        </div>
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">Phone</h4>
                          <div className="space-y-0.5">
                            <a href="tel:+97444111144" className="block text-sm text-gray-700 dark:text-gray-300 hover:text-maroon-primary dark:hover:text-maroon-dark transition-colors">
                              +974 44111144
                            </a>
                            <a href="tel:+97451514777" className="block text-sm text-gray-700 dark:text-gray-300 hover:text-maroon-primary dark:hover:text-maroon-dark transition-colors">
                    +974 51514777
                            </a>
                          </div>
                        </div>
                      </div>

                      {/* Email */}
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-maroon-primary/10 dark:bg-maroon-primary/20 flex items-center justify-center flex-shrink-0">
                          <Mail className="w-4 h-4 text-maroon-primary" />
                </div>
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">Email</h4>
                          <a href="mailto:info@eshharrealestate.com" className="text-sm text-gray-700 dark:text-gray-300 hover:text-maroon-primary dark:hover:text-maroon-dark transition-colors">
                    info@eshharrealestate.com
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quote Form Modal */}
        {showEnquireForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="relative w-full max-w-4xl max-h-[90vh] bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold dark:text-white">Inquiry Form</h3>
                <button
                  onClick={() => setShowEnquireForm(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <iframe
                className="clickup-embed clickup-dynamic-height w-full"
                src="https://forms.clickup.com/9007020946/p/f/8cdrewj-29578/6YWX6PO1SD7Z9Q2BCR/customer-intake-form"
                onWheel={() => { }}
                width="100%"
                height="600"
                style={{ background: 'transparent', border: 'none' }}
                title="Customer Intake Form"
              />
            </div>
          </div>
        )}

        {/* Bug Submission Form Modal */}
        {showBugForm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 overflow-y-auto custom-scrollbar">
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 max-w-2xl w-full mx-4 my-8 shadow-xl">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold dark:text-white">Submit a Bug</h2>
                <button
                  onClick={() => setShowBugForm(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="h-[600px] overflow-hidden rounded">
                <iframe
                  className="clickup-embed w-full h-full"
                  src="https://forms.clickup.com/9007020946/f/8cdrewj-17378/LKZ3D765NNUW3BVBL4?Identifier=Form%20Ticket&Customer=9e1c2387-b2ba-425a-8b70-8c54fc20ea7e"
                  style={{
                    background: "transparent",
                    border: "none",
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}
